import { CloseIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  GridItem,
  SimpleGrid,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import { useMemo } from "react";
import countryList from "react-select-country-list";
import { useReferenceOptions } from "../../../db/useReferenceOptions";
import { DataCard } from "./DatCard";

export function formatItemPreviewValue(
  item,
  countryOptions = [],
  referenceOptions = {}
) {
  const previewKeys = item?.PREVIEW?.PREVIEW_VALUE || [];

  const rawValues = previewKeys.map((key) => {
    const field = item[key];
    const value = field?.VALUE;
    if (!value) return "";

    // Handle Dropdown with inline ENUM
    if (field?.TYPE === "Dropdown") {
      if (Array.isArray(field.ENUM)) {
        const match = field.ENUM.find((entry) => entry.CODE === value);
        if (match) return match.TEXT;
      }

      // Handle Dropdown from IndexedDB references
      const refCode = field?.REFERENCE?.CODE;
      const subgroup = field?.REFERENCE?.SUBGROUP;
      const keyName = subgroup ? `${refCode}:::${subgroup}` : refCode;
      const refList = referenceOptions[keyName];

      if (refList) {
        const match = refList.find((opt) => opt.value === value);
        if (match) return match.label;
      }
    }

    // Handle Country
    if (field?.TYPE === "Country" && countryOptions.length > 0) {
      const match = countryOptions.find((option) => option.value === value);
      if (match) return match.label;
    }

    // Format YYYYMMDD date
    if (typeof value === "string") {
      const match = value.match(/\b\d{8}\b/);
      if (match) {
        const formattedDate = moment(match[0], "YYYYMMDD").format("YYYY-MM-DD");
        return value.replace(match[0], formattedDate);
      }
    }

    return value;
  });

  return rawValues.filter(Boolean).join(", ");
}

export const DataAccordion = ({
  data,
  tabKey,
  fieldKey,
  onArrayChange,
  onDeleteItem,
}) => {
  const countryOptions = useMemo(() => countryList().getData(), []);
  const referenceOptions = useReferenceOptions();
  return (
    <Accordion allowToggle allowMultiple defaultIndex={[0]}>
      {data.map((item, index) => (
        <AccordionItem key={index}>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left" textTransform="revert-layer">
                {formatItemPreviewValue(
                  item,
                  countryOptions,
                  referenceOptions
                ) || ""}
              </Box>
              <Button
                border="none"
                borderRadius="full"
                size="xs"
                colorScheme="red"
                ml={2}
                mr={2}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteItem(index);
                }}
              >
                <CloseIcon w={2} h={2} />
              </Button>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
              {Object.entries(item)
                .filter(([key]) => key !== "PREVIEW")
                .map(([subFieldKey, value]) => {
                  if (
                    !value ||
                    typeof value !== "object" ||
                    !("MESSAGE" in value) ||
                    !("VALUE" in value)
                  ) {
                    return null;
                  }

                  const isTextBox = value.TYPE === "TextBox";

                  return (
                    <GridItem key={subFieldKey} colSpan={isTextBox ? 4 : 1}>
                      <DataCard
                        label={value.MESSAGE}
                        tooltip={value.TOOLTIP}
                        code={value.REFERENCE?.CODE}
                        referenceMap={value.REFERENCE}
                        type={value.TYPE}
                        value={value.VALUE}
                        multiSelectType={value.MULTISELECT_TYPE}
                        isEditable={value?.EDITABLE}
                        isVisible={value?.VISIBLE}
                        onChange={(val) =>
                          onArrayChange(
                            tabKey,
                            fieldKey,
                            index,
                            subFieldKey,
                            val
                          )
                        }
                        enumOptions={
                          value.TYPE === "Dropdown" ? value.ENUM : undefined
                        }
                      />
                    </GridItem>
                  );
                })}
            </SimpleGrid>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
